import React from 'react';
import { plural, t } from '@lingui/core/macro';
import { Location } from 'history';

import Avatar, { AvatarType, Size } from '@rover/components/src/Avatar/Avatar';
import CalloutBadge from '@rover/components/src/callouts/CalloutBadge';
import SimplifiedRatingsReview from '@rover/components/src/RatingsReview/SimplifiedRatingsReview';
import useShortlist from '@rover/components/src/shortlist/hooks/useShortlist';
import SitterHighlight from '@rover/components/src/SitterHighlight/SitterHighlight';
import Rebook from '@rover/icons/svg/rebook.svg';
import { Box, Display, Flex } from '@rover/kibble/core';
import { highlightNumberInText } from '@rover/react-lib/src/pages/search/SearchPage/utilities/searchCardTreatment';
import BackToSearchButton from '@rover/react-lib/src/pages/sitter-profile/components/BackToSearchButton';
import FavoriteButton from '@rover/react-lib/src/pages/sitter-profile/components/FavoriteButton';
import InlineExperienceHighlight from '@rover/react-lib/src/pages/sitter-profile/SitterProfilePage/components/ExperienceHighlight/InlineExperienceHighlight';
import { showExperienceHighlight } from '@rover/react-lib/src/pages/sitter-profile/SitterProfilePage/utils/utils';
import type { EnhancedPersonSitterProfile } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfile';

import {
  getRepeatCount,
  getShouldShowFavoriteButton,
  LINE_CLAMP_2_STYLES,
} from './profileHeaderUtils';

export type MobileProfileHeaderProps = {
  sitterInfo: EnhancedPersonSitterProfile;
  sitterImage: string;
  ownProfile: boolean;
  isApproved: boolean;
  onAvatarClick: (index: number) => void;
  onError: (error: string) => void;
  location: Location;
  service?: string;
  searchParams: URLSearchParams;
  isLoggedIn?: boolean;
  onReviewsCountPress?: () => void;
};

const MobileProfileHeader = ({
  sitterInfo,
  sitterImage,
  ownProfile,
  isApproved,
  onAvatarClick,
  onError,
  location,
  service,
  searchParams,
  isLoggedIn,
  onReviewsCountPress,
}: MobileProfileHeaderProps): JSX.Element => {
  const repeatCount = getRepeatCount(sitterInfo);
  const { isVisible: isShortlistVisible } = useShortlist();
  const areAllServicesUnapproved =
    sitterInfo.services.length > 0 && sitterInfo.services.every((s) => !s.isApproved);
  const shouldShowFavoriteButton = getShouldShowFavoriteButton(
    sitterInfo,
    ownProfile,
    isApproved,
    location,
    isShortlistVisible
  );

  const inlineExperienceOrRatings = showExperienceHighlight(sitterInfo) ? (
    <InlineExperienceHighlight sitter={sitterInfo} iconSize={16} />
  ) : (
    <SimplifiedRatingsReview
      ratingsAverage={sitterInfo.ratingsAverage}
      reviewsCount={sitterInfo.reviewsCount}
      onReviewsCountPress={onReviewsCountPress}
    />
  );

  return (
    <Box width="100%" pt="6x" pb="4x" gap="4x" display="flex">
      <Box flexShrink="0" sx={{ cursor: 'pointer' }} onClick={() => onAvatarClick(0)}>
        <Avatar
          diameter="100px"
          size={Size.Profile}
          borderRadius="8px"
          avatarType={AvatarType.Person}
          imageUrl={sitterImage}
        />
      </Box>
      <Flex flexDirection="column" flexGrow="1" minWidth="0">
        <Flex justifyContent="space-between" alignItems="flex-start">
          <Display size="400" mb="0x" sx={LINE_CLAMP_2_STYLES}>
            {sitterInfo.shortName}
          </Display>
          {shouldShowFavoriteButton && (
            <Flex alignItems="center" flexShrink="0" height="8x">
              <FavoriteButton
                sitterInfo={sitterInfo}
                isLoggedIn={isLoggedIn}
                onError={onError}
                location={location}
                size="small"
              />
            </Flex>
          )}
        </Flex>
        {isApproved && !areAllServicesUnapproved ? (
          <>
            {inlineExperienceOrRatings}
            {repeatCount > 0 && (
              <SitterHighlight
                dataTestId="mobile-repeat-clients-highlight"
                text={highlightNumberInText(repeatCount.toString())}
                icon={Rebook}
                ariaLabel={plural(repeatCount, {
                  one: '# repeat client',
                  other: '# repeat clients',
                })}
              />
            )}
            {!ownProfile && (
              <Flex>
                <BackToSearchButton
                  shouldShowRevampLayout
                  serviceType={service}
                  searchParams={searchParams}
                  size="small"
                />
              </Flex>
            )}
          </>
        ) : (
          <Flex>
            <CalloutBadge
              data-testid="header-coming-soon-badge"
              value={t`Coming Soon`}
              calloutBadgeStyle={CalloutBadge.Styles.COMING_SOON}
            />
          </Flex>
        )}
      </Flex>
    </Box>
  );
};

export default MobileProfileHeader;
